import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import SettingCard, { accountSettingsFieldRowClassName } from "./SettingCard";
import { toastError } from "../../../../utils/showApiErrorToast";

const DisplayNameSetting: React.FC = () => {
  const { user } = useUser();
  const [displayName, setDisplayName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialName, setInitialName] = useState("");

  useEffect(() => {
    if (user) {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      setDisplayName(fullName || "");
      setInitialName(fullName || "");
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    const nameParts = displayName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    if (displayName.length > 32) {
      toastError("Name too long", { description: "Please use 32 characters at maximum." });
      return;
    }

    setIsSaving(true);
    try {
      await user.update({
        firstName,
        lastName,
      });

      toast.success("Display name updated", {
        description: "Your display name has been updated successfully.",
      });
      setInitialName(displayName.trim());
      setIsEditing(false);
    } catch (error: unknown) {
      const err = error as { errors?: Array<{ message?: string }> };
      toastError("Update failed", {
        description: err?.errors?.[0]?.message || "Failed to update display name. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingCard
      title="Display Name"
      description="Please enter your full name, or a display name you are comfortable with."
    >
      <div className={accountSettingsFieldRowClassName}>
        <Input
          id="display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Enter your display name"
          maxLength={32}
          readOnly={!isEditing}
          className={cn(
            "min-w-0 flex-1",
            !isEditing && "cursor-not-allowed bg-muted/50"
          )}
          data-font="primary"
        />
        {isEditing ? (
          <div className="flex shrink-0 flex-row gap-2">
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isSaving}
              aria-busy={isSaving || undefined}
              onClick={() => void handleSave()}
            >
              {isSaving ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Save
            </Button>
            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isSaving}
              onClick={() => {
                setDisplayName(initialName);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0"
            onClick={() => setIsEditing(true)}
          >
            Update
          </Button>
        )}
      </div>
    </SettingCard>
  );
};

export default DisplayNameSetting;
